import isNil from 'lodash/isNil'
import type { DateType } from '@ztechapps/common.utils'
import { toDate } from '@ztechapps/common.utils'

/**
 * Heals a shipping date that is nil or stale so downstream consumers never
 * forward a past date (e.g. carrier rate requests that reject past ship dates).
 *
 * A shipping date is a calendar DATE (a day), not an instant — so the healed
 * value is today's date anchored to `timeZone` at start-of-day, NOT the current
 * timestamp. `timeZone` is the tenant/warehouse tz on the backend and the user's
 * tz in the UI; it anchors both the past/today comparison AND the healed day, so
 * "today" is the tenant's/user's calendar day regardless of where the server runs
 * (runtime-local when `timeZone` is omitted).
 *
 * - Nil, or before today (day granularity, in `timeZone`) → start of today in `timeZone`.
 * - Today or later → returned unchanged.
 */
export const normalizeShippingDate = (date?: DateType, timeZone?: string): Date => {
  const isNilOrPast = isNil(date) || toDate(date, timeZone).isBefore(toDate(undefined, timeZone), 'day')

  if (isNilOrPast) return toDate(undefined, timeZone).startOf('day').toDate()

  return toDate(date).toDate()
}
